// =============================================================================
// Public Booking Page — /book/:tenantSlug (no auth)
// =============================================================================
// Wizard pubblico per la prenotazione dei servizi. Riusa react-i18next già
// inizializzato lato app; usa publicApiClient (no Authorization, no x-tenant-id).
// Il widget embed riusa gli stessi step components da components/publicBooking.
// =============================================================================

import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AlertCircle, Calendar, Loader2 } from 'lucide-react';
import {
  usePublicTenantInfo,
  usePublicServices,
  useSubmitPublicBooking,
  type PublicService,
  type SubmitBookingPayload,
  type SubmitBookingResult,
} from '../../api/public-booking-hooks';
import { ServiceStep } from '../../components/publicBooking/ServiceStep';
import { SlotStep } from '../../components/publicBooking/SlotStep';
import { DetailsStep, type CustomerDetails } from '../../components/publicBooking/DetailsStep';
import { ConfirmStep } from '../../components/publicBooking/ConfirmStep';
import { ResultStep } from '../../components/publicBooking/ResultStep';
import { PublicBookingLayout } from '../../components/publicBooking/PublicBookingLayout';
import { Stepper } from '../../components/publicBooking/Stepper';

type StepId = 'service' | 'slot' | 'details' | 'confirm' | 'done';

const STEPS: StepId[] = ['service', 'slot', 'details', 'confirm', 'done'];

export function PublicBookingPage() {
  const { tenantSlug } = useParams<{ tenantSlug: string }>();
  const { t, i18n } = useTranslation('public-booking');
  const [searchParams] = useSearchParams();
  const isEmbed = searchParams.get('embed') === '1';
  const embedColor = searchParams.get('color');
  const presetService = searchParams.get('service');
  const presetLng = searchParams.get('lng');

  // Embed mode: invia altezza al parent widget tramite postMessage
  const containerRef = useRef<HTMLDivElement | null>(null);
  useLayoutEffect(() => {
    if (!isEmbed) return;
    const send = () => {
      const h = document.documentElement.scrollHeight;
      window.parent.postMessage({ type: 'skedulab:resize', height: h }, '*');
    };
    send();
    const ro = new ResizeObserver(send);
    if (containerRef.current) ro.observe(containerRef.current);
    return () => ro.disconnect();
  });

  // Override lingua se viene da widget embed con `?lng=`
  useEffect(() => {
    if (presetLng && presetLng !== i18n.language) {
      void i18n.changeLanguage(presetLng);
    }
  }, [presetLng, i18n]);

  const tenantQuery = usePublicTenantInfo(tenantSlug);
  const servicesQuery = usePublicServices(tenantSlug);
  const submitMutation = useSubmitPublicBooking(tenantSlug);

  const [step, setStep] = useState<StepId>('service');
  const [selectedService, setSelectedService] = useState<PublicService | null>(null);
  const [selectedStart, setSelectedStart] = useState<string | null>(null);
  const [preferredAllocations, setPreferredAllocations] = useState<
    Array<{ resourceTypeId: string; resourceId?: string }>
  >([]);
  const [customer, setCustomer] = useState<CustomerDetails | null>(null);
  const [honeypot, setHoneypot] = useState('');
  const [result, setResult] = useState<SubmitBookingResult | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);

  // Brand color → CSS custom property. L'override via query (embed widget) ha
  // priorità sul branding del tenant.
  const primaryColor = embedColor ?? tenantQuery.data?.branding.primaryColor ?? '#10B981';

  useEffect(() => {
    document.documentElement.style.setProperty('--skedulab-public-primary', primaryColor);
    return () => {
      document.documentElement.style.removeProperty('--skedulab-public-primary');
    };
  }, [primaryColor]);

  const stepIndex = STEPS.indexOf(step);
  const canGoBack = stepIndex > 0 && step !== 'done';

  const goBack = () => {
    if (!canGoBack) return;
    const prev = STEPS[stepIndex - 1];
    if (prev) setStep(prev);
  };

  const handleServiceSelect = (svc: PublicService) => {
    setSelectedService(svc);
    setPreferredAllocations([]);
    setSelectedStart(null);
    setStep('slot');
  };

  // Pre-seleziona automaticamente il servizio se passato via ?service=
  useEffect(() => {
    if (!presetService || selectedService) return;
    const svc = servicesQuery.data?.find((s) => s.id === presetService);
    if (svc) handleServiceSelect(svc);
    // handleServiceSelect è stabile per scope di componente
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [presetService, servicesQuery.data]);

  const handleSlotConfirm = (
    startIso: string,
    allocations: Array<{ resourceTypeId: string; resourceId?: string }>,
  ) => {
    setSelectedStart(startIso);
    setPreferredAllocations(allocations);
    setStep('details');
  };

  const handleDetailsConfirm = (data: CustomerDetails) => {
    setCustomer(data);
    setStep('confirm');
  };

  const handleSubmit = async (acceptTerms: boolean) => {
    if (!selectedService || !selectedStart || !customer || !tenantSlug) return;
    setSubmitError(null);
    const payload: SubmitBookingPayload = {
      serviceId: selectedService.id,
      startTime: selectedStart,
      preferredAllocations: preferredAllocations.length > 0 ? preferredAllocations : undefined,
      customer,
      acceptTerms,
      locale: i18n.language,
      source: 'web',
      _hp: honeypot,
      // captchaToken: in v1 nessun provider implementato
    };
    try {
      const res = await submitMutation.mutateAsync(payload);
      setResult(res);
      setStep('done');
    } catch (e: unknown) {
      const code = (e as { response?: { data?: { code?: string } } }).response?.data?.code;
      if (code === 'PUBLIC_BOOKING_SLOT_UNAVAILABLE') {
        setSubmitError(t('errors.slotUnavailable'));
        setStep('slot');
      } else if (code === 'PUBLIC_BOOKING_CAPTCHA_REQUIRED' || code === 'PUBLIC_BOOKING_CAPTCHA_INVALID') {
        setSubmitError(t('errors.captchaRequired'));
      } else if (code === 'PUBLIC_BOOKING_OUT_OF_WINDOW') {
        setSubmitError(t('errors.outOfWindow'));
        setStep('slot');
      } else {
        setSubmitError(t('errors.genericError'));
      }
    }
  };

  const stepLabels = useMemo(
    () => STEPS.slice(0, 4).map((s) => t(`steps.${s}`)),
    [t],
  );

  // ===========================================================================
  // Stati di caricamento / errore
  // ===========================================================================

  if (tenantQuery.isLoading) {
    return (
      <PublicBookingLayout tenant={null}>
        <div className="flex items-center justify-center py-20 text-gray-500">
          <Loader2 className="w-6 h-6 animate-spin mr-2" /> {t('loading')}
        </div>
      </PublicBookingLayout>
    );
  }

  if (tenantQuery.isError || !tenantQuery.data) {
    return (
      <PublicBookingLayout tenant={null}>
        <ErrorView
          title={t('errors.tenantNotFound')}
          description={t('errors.tenantNotFoundHint')}
        />
      </PublicBookingLayout>
    );
  }

  const tenant = tenantQuery.data;

  return (
    <PublicBookingLayout tenant={tenant} embed={isEmbed} containerRef={containerRef}>
      {/* Honeypot: campo nascosto, mai mostrato all'utente reale */}
      <input
        type="text"
        name="_hp"
        value={honeypot}
        onChange={(e) => setHoneypot(e.target.value)}
        autoComplete="off"
        tabIndex={-1}
        aria-hidden="true"
        className="absolute -left-[10000px] w-0 h-0 overflow-hidden"
      />

      {step !== 'done' && (
        <Stepper labels={stepLabels} currentIndex={stepIndex} />
      )}

      {submitError && step !== 'done' && (
        <div className="mb-4 rounded-md border border-red-200 bg-red-50 p-3 flex items-start gap-2">
          <AlertCircle className="w-4 h-4 text-red-600 mt-0.5" />
          <p className="text-sm text-red-700">{submitError}</p>
        </div>
      )}

      {step === 'service' && (
        <ServiceStep
          services={servicesQuery.data ?? []}
          isLoading={servicesQuery.isLoading}
          onSelect={handleServiceSelect}
        />
      )}

      {step === 'slot' && selectedService && (
        <SlotStep
          tenantSlug={tenantSlug!}
          service={selectedService}
          tenant={tenant}
          initialStart={selectedStart}
          initialAllocations={preferredAllocations}
          onBack={goBack}
          onConfirm={handleSlotConfirm}
        />
      )}

      {step === 'details' && (
        <DetailsStep
          initial={customer}
          onBack={goBack}
          onSubmit={handleDetailsConfirm}
        />
      )}

      {step === 'confirm' && selectedService && selectedStart && customer && (
        <ConfirmStep
          service={selectedService}
          startIso={selectedStart}
          customer={customer}
          tenant={tenant}
          onBack={goBack}
          onSubmit={handleSubmit}
          isSubmitting={submitMutation.isPending}
        />
      )}

      {step === 'done' && result && (
        <ResultStep
          result={result}
          onAgain={() => {
            setSelectedService(null);
            setSelectedStart(null);
            setCustomer(null);
            setResult(null);
            setSubmitError(null);
            setStep('service');
          }}
        />
      )}
    </PublicBookingLayout>
  );
}

function ErrorView({ title, description }: { title: string; description: string }) {
  return (
    <div className="flex flex-col items-center justify-center py-20 text-center">
      <div className="w-14 h-14 rounded-full bg-red-50 flex items-center justify-center mb-3">
        <Calendar className="w-7 h-7 text-red-500" />
      </div>
      <h2 className="text-lg font-semibold text-gray-900">{title}</h2>
      <p className="text-sm text-gray-500 mt-1 max-w-md">{description}</p>
    </div>
  );
}

export default PublicBookingPage;
